import { useCallback, useEffect, useState } from 'react';
import { Check2, Plus } from 'react-bootstrap-icons';
import { Modal, ModalProps } from 'react-bootstrap';

import { Avatar, LoadingButton } from '@/components';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { ToolAuthorModel, ToolModel, ToolPreviewModel } from '@/lib/models';
import { advisorService } from '@/lib/services';

type ToolAboutModel = ToolModel | ToolPreviewModel;

interface ToolAboutModalProps extends ModalProps {
	tool?: ToolAboutModel;
	onToolChange?(tool: ToolAboutModel): void;
}

const useStyles = createUseThemedStyles((theme) => ({
	dialog: {
		'& .modal-content': {
			border: 0,
			borderRadius: 0,
			boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
		},
		'& .modal-header': {
			minHeight: 76,
			alignItems: 'center',
			padding: '0 36px',
			borderBottom: `1px solid ${theme.colors.gray300}`,
		},
		'& .modal-title': {
			...theme.fonts.s,
			...theme.fonts.openSansBold,
			color: theme.colors.black,
		},
		'& .modal-body': {
			padding: '24px 36px 32px',
		},
	},
	headerMeta: {
		marginLeft: 'auto',
		marginRight: 18,
		color: theme.colors.gray600,
		...theme.fonts.s,
	},
	section: {
		'& + &': {
			marginTop: 22,
			paddingTop: 22,
			borderTop: `1px solid ${theme.colors.gray300}`,
		},
	},
	sectionTitle: {
		marginBottom: 12,
		...theme.fonts.xs,
		...theme.fonts.openSansBold,
		color: theme.colors.black,
	},
	text: {
		margin: 0,
		whiteSpace: 'pre-line',
		color: theme.colors.gray800,
		...theme.fonts.xs,
	},
	authorList: {
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	author: {
		minHeight: 72,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 18,
		padding: '12px 0',
		'& + &': {
			borderTop: `1px solid ${theme.colors.gray300}`,
		},
		'@media (max-width: 575px)': {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	authorIdentity: {
		minWidth: 0,
		flex: '1 1 auto',
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		overflow: 'hidden',
	},
	authorText: {
		minWidth: 0,
	},
	authorName: {
		margin: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		...theme.fonts.xs,
		...theme.fonts.openSansBold,
		color: theme.colors.gray900,
	},
	authorPosition: {
		display: 'block',
		marginTop: 2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: theme.colors.gray600,
		...theme.fonts.xxs,
	},
	followButton: {
		flex: '0 0 auto',
		minWidth: 126,
		whiteSpace: 'nowrap',
	},
}));

const updateAuthorFollowState = (authors: ToolAuthorModel[], advisorId: string, userIsFollowing: boolean) =>
	authors.map((author) => (author.advisorId === advisorId ? { ...author, userIsFollowing } : author));

export const ToolAboutModal = ({ tool, onToolChange, ...props }: ToolAboutModalProps) => {
	const classes = useStyles();
	const handleError = useHandleError();
	const [authors, setAuthors] = useState<ToolAuthorModel[]>([]);
	const [authorLoadingById, setAuthorLoadingById] = useState<Record<string, boolean>>({});

	useEffect(() => {
		setAuthors(tool?.authors ?? []);
	}, [tool]);

	const toggleFollow = useCallback(
		async (advisorId: string, follow: boolean) => {
			setAuthorLoadingById((current) => ({ ...current, [advisorId]: true }));
			try {
				await advisorService.followAuthor({ authorId: advisorId, follow }).fetch();
				const nextAuthors = updateAuthorFollowState(authors, advisorId, follow);
				setAuthors(nextAuthors);
				if (tool) {
					onToolChange?.({ ...tool, authors: nextAuthors });
				}
			} catch (error) {
				handleError(error);
			} finally {
				setAuthorLoadingById((current) => ({ ...current, [advisorId]: false }));
			}
		},
		[authors, handleError, onToolChange, tool]
	);

	return (
		<Modal dialogClassName={classes.dialog} centered {...props}>
			<Modal.Header closeButton>
				<Modal.Title>About</Modal.Title>
				{tool?.versionLabel && <span className={classes.headerMeta}>Version {tool.versionLabel}</span>}
			</Modal.Header>
			<Modal.Body>
				<section className={classes.section}>
					<h2 className={classes.sectionTitle}>Description</h2>
					{tool?.description ? (
						<div
							className={`html-editor ${classes.text}`}
							dangerouslySetInnerHTML={{ __html: tool.description }}
						/>
					) : (
						<p className={classes.text}>Description is not available.</p>
					)}
				</section>
				{tool?.releaseNotes && (
					<section className={classes.section}>
						<h2 className={classes.sectionTitle}>In this version</h2>
						<p className={classes.text}>{tool.releaseNotes}</p>
					</section>
				)}
				<section className={classes.section}>
					<h2 className={classes.sectionTitle}>Authors</h2>
					{authors.length ? (
						<ul className={classes.authorList}>
							{authors.map((author, index) => (
								<li key={author.advisorId ?? `${author.name}-${index}`} className={classes.author}>
									<div className={classes.authorIdentity}>
										<Avatar size={48} url={author.imageUrl} />
										<div className={classes.authorText}>
											<h3 className={classes.authorName}>{author.name}</h3>
											<span className={classes.authorPosition}>
												{author.position || 'Position/Title'}
											</span>
										</div>
									</div>
									{author.advisorId && (
										<LoadingButton
											isLoading={Boolean(authorLoadingById[author.advisorId])}
											className={classes.followButton}
											classNameInner="d-flex align-items-center justify-content-center"
											variant={author.userIsFollowing ? 'primary' : 'outline-primary'}
											onClick={() => toggleFollow(author.advisorId!, !author.userIsFollowing)}
										>
											{author.userIsFollowing ? (
												<>
													<Check2 className="mr-2" size={18} />
													Following
												</>
											) : (
												<>
													<Plus className="mr-2" size={18} />
													Follow
												</>
											)}
										</LoadingButton>
									)}
								</li>
							))}
						</ul>
					) : (
						<p className={classes.text}>Author information is not available.</p>
					)}
				</section>
			</Modal.Body>
		</Modal>
	);
};
